import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../hooks/useAuth'
import {
  Search,
  Clock,
  Target,
  List,
  Star,
  Users,
  Lock,
  Play,
  BarChart3,
  Settings,
  ChevronDown,
  Eye,
  Heart,
  TrendingUp,
  RotateCcw,
  BookOpen,
  Plus
} from 'lucide-react'
import { RootState } from '../store'
import {
  fetchDecks,
  setCurrentDeck,
  searchFlashcards,
  setFilter,
  clearSearchResults
} from '../store/slices/flashcardSlice'
import { FlashcardDeck, FlashcardFilter } from '../types/flashcard'
import { LGS_SUBJECTS, getSubjectByValue, DIFFICULTY_LEVELS } from '../constants/lgsSubjects'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
// LoadingSpinner removed - no loading state needed
import ImportFlashcards from '../components/ImportFlashcards'

const Flashcards: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const {
    decks,
    isLoading: loading,
    error,
    filter,
    searchResults
  } = useSelector((state: RootState) => state.flashcard)
  const { loading: authLoading } = useSelector((state: RootState) => state.auth)
  const isRehydrated = useSelector((state: RootState) => state._persist?.rehydrated)

  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [localFilter, setLocalFilter] = useState<FlashcardFilter>({
    subject: '',
    topic: '',
    difficulty: undefined,
    isPublic: undefined
  })

  // LGS subjects from constants
  const subjects = LGS_SUBJECTS

  // Fetch decks when user is authenticated and after rehydration
  useEffect(() => {
    if (isAuthenticated && isRehydrated) {
      dispatch(fetchDecks() as any)
    }
  }, [isAuthenticated, isRehydrated, dispatch])

  useEffect(() => {
    if (searchQuery.trim()) {
      const timer = setTimeout(() => {
        dispatch(searchFlashcards({ query: searchQuery, filter: localFilter }) as any)
      }, 500)
      return () => clearTimeout(timer)
    } else {
      // Clear search results when search query is empty
      dispatch(clearSearchResults())
    }
  }, [searchQuery, localFilter, dispatch])

  const handleFilterChange = (newFilter: Partial<FlashcardFilter>) => {
    const updatedFilter = { ...localFilter, ...newFilter }
    setLocalFilter(updatedFilter)
    dispatch(setFilter(updatedFilter))
  }

  const handleSubjectFilter = (subject: string) => {
    const updatedFilter = {
      subject,
      topic: '',
      difficulty: undefined,
      isPublic: undefined
    }
    setLocalFilter(updatedFilter)
    dispatch(setFilter(updatedFilter))
  }

  const handleDeckSelect = (deck: FlashcardDeck) => {
    dispatch(setCurrentDeck(deck))
  }

  const getSubjectColor = (subject: string) => {
    const subjectData = getSubjectByValue(subject)
    return subjectData?.color || 'bg-gray-500'
  }

  const getSubjectLabel = (subject: string) => {
    const subjectData = getSubjectByValue(subject)
    return subjectData?.label || subject
  }

  const getSubjectIcon = (subject: string) => {
    const subjectData = getSubjectByValue(subject)
    return subjectData?.icon || '📚'
  }

  const getDifficultyColor = (difficulty: string) => {
    const difficultyData = DIFFICULTY_LEVELS.find(d => d.value === difficulty)
    return difficultyData?.color || 'bg-gray-100 text-gray-800'
  }

  const getDifficultyLabel = (difficulty: string) => {
    const difficultyData = DIFFICULTY_LEVELS.find(d => d.value === difficulty)
    return difficultyData?.label || difficulty
  }

  // Helper function to get topic order from LGS_SUBJECTS
  const getTopicOrder = (subject: string, topic: string) => {
    const subjectData = LGS_SUBJECTS.find(s => s.value === subject);
    if (!subjectData) return 999; // Unknown subjects go to end
    
    const topicIndex = subjectData.topics.findIndex(t => t.value === topic);
    return topicIndex === -1 ? 999 : topicIndex; // Unknown topics go to end
  };

  // Helper function to get subject order from LGS_SUBJECTS
  const getSubjectOrder = (subject: string) => {
    const subjectIndex = LGS_SUBJECTS.findIndex(s => s.value === subject);
    return subjectIndex === -1 ? 999 : subjectIndex; // Unknown subjects go to end
  };

  // Apply filters to decks
  const filteredDecks = decks.filter(deck => {
    // Subject filter
    if (localFilter.subject && deck.subject !== localFilter.subject) {
      return false;
    }
    
    // Difficulty filter
    if (localFilter.difficulty && deck.difficulty !== localFilter.difficulty) {
      return false;
    }
    
    // Public/Private filter
    if (localFilter.isPublic !== undefined && deck.isPublic !== localFilter.isPublic) {
      return false;
    }
    
    // Topic filter
    if (localFilter.topic && deck.topic !== localFilter.topic) {
      return false;
    }
    
    return true;
  }).sort((a, b) => {
    // If "Tüm Dersler" is selected (no subject filter), sort by subject order first, then by topic order
    if (!localFilter.subject) {
      const subjectOrderA = getSubjectOrder(a.subject);
      const subjectOrderB = getSubjectOrder(b.subject);
      
      if (subjectOrderA !== subjectOrderB) {
        return subjectOrderA - subjectOrderB;
      }
      
      // If same subject, sort by topic order
      const topicOrderA = getTopicOrder(a.subject, a.topic);
      const topicOrderB = getTopicOrder(b.subject, b.topic);
      return topicOrderA - topicOrderB;
    }
    
    // If a specific subject is selected, sort by topic order within that subject
    const topicOrderA = getTopicOrder(a.subject, a.topic);
    const topicOrderB = getTopicOrder(b.subject, b.topic);
    return topicOrderA - topicOrderB;
  });
  
  const displayDecks = searchQuery.trim() ? searchResults?.decks || [] : filteredDecks

  // Show loading spinner during initial deck fetch
  if (authLoading || (isAuthenticated && loading && decks.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Desteler yükleniyor...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Giriş Yapmanız Gerekiyor</h2>
          <p className="text-gray-600 mb-6">Flashcard'larınızı görüntülemek için lütfen giriş yapın.</p>
          <Link to="/login">
            <Button className="bg-blue-600 hover:bg-blue-700">
              Giriş Yap
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent">
      {/* Modern Header */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-3xl p-8 text-white mb-8">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex flex-col gap-4 lg:gap-6">
                  <div>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                      Flashcard Destelerim
                    </h1>
                    <p className="text-blue-100 text-sm sm:text-base lg:text-lg">
                      LGS konularını flashcard'larla çalış ve başarıya ulaş
                    </p>
                  </div>
                  
                  {/* Modern Search and Controls */}
                  <div className="flex flex-col gap-4">
                    <div className="relative w-full">
                      <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-blue-200 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        placeholder="Deste veya kart ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      />
                    </div>
                    
                    {/* Subject Filter Buttons with Review Button */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleSubjectFilter('')}
                          className={`px-2 sm:px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 ${
                            localFilter.subject === '' 
                              ? 'bg-white text-blue-600 shadow-lg' 
                              : 'bg-white/20 text-blue-100 hover:bg-white/30'
                          }`}
                        >
                          Tüm Dersler
                        </button>
                        {subjects.map((subject) => (
                          <button
                            key={subject.value}
                            onClick={() => handleSubjectFilter(subject.value)}
                            className={`px-2 sm:px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 ${
                              localFilter.subject === subject.value 
                                ? 'bg-white text-blue-600 shadow-lg' 
                                : 'bg-white/20 text-blue-100 hover:bg-white/30'
                            }`}
                          >
                            {subject.label}
                          </button>
                        ))}
                      </div>
                      
                      <div className="flex justify-center sm:justify-end flex-shrink-0">
                        <button
                          onClick={() => setShowReviewModal(true)}
                          className="group relative overflow-hidden px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl text-sm font-bold transition-all duration-300 transform hover:scale-105 hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl w-full sm:w-auto"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <RotateCcw className="w-4 h-4" />
                            <span>Tekrar Et</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full translate-y-24 -translate-x-24"></div>
          </div>
        </div>



      </div>

      {/* Error State */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6"
        >
          <div className="bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm border border-red-200 dark:border-red-800 rounded-3xl p-6">
            <p className="text-red-800 dark:text-red-200 font-medium">{error}</p>
          </div>
        </motion.div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">

        {/* Modern Decks Grid/List */}
        {displayDecks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-12 max-w-md mx-auto border border-gray-200/60 dark:border-gray-700/60">
              <BookOpen className="w-16 h-16 text-blue-500 mx-auto mb-6" />
              <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {searchQuery ? 'Arama sonucu bulunamadı' : 'Henüz deste oluşturmamışsın'}
              </h3>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 mb-6">
                {searchQuery 
                  ? 'Farklı anahtar kelimeler deneyin veya filtreleri değiştirin'
                  : 'İlk flashcard desteni oluşturarak çalışmaya başla'
                }
              </p>
              {!searchQuery && (
                <Link to="/flashcards/create">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-sm font-medium rounded-xl transition-all duration-200">
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">İlk Desteni Oluştur</span>
                      <span className="sm:hidden">Oluştur</span>
                    </div>
                  </button>
                </Link>
              )}
            </div>
          </motion.div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {displayDecks.map((deck, index) => (
              <motion.div
                key={deck.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => handleDeckSelect(deck)}
              >
                {/* Modern List View */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-5 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200/60 dark:border-gray-700/60 relative overflow-hidden">
                    {/* Subtle Background */}
                    <div className="absolute inset-0 bg-gray-50/20 dark:bg-gray-700/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    
                    <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-3 sm:gap-4 flex-1">
                        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${getSubjectColor(deck.subject)} flex items-center justify-center text-lg sm:text-xl font-bold shadow-sm flex-shrink-0`}>
                          <span className="text-white filter brightness-0 invert">{getSubjectIcon(deck.subject)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2 leading-tight truncate">
                            {deck.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span className={`px-2 py-1 rounded-lg text-xs font-medium text-white ${getSubjectColor(deck.subject)}`}>
                              {getSubjectLabel(deck.subject)}
                            </span>
                            {deck.topic && (
                              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700/60 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium">
                                {deck.topic}
                              </span>
                            )}
                            <span className="flex items-center gap-1.5">
                              <Target size={12} className="text-blue-500" />
                              <span className="font-medium text-xs">{deck.totalCards} kart</span>
                            </span>
                            <span className={`px-2 py-1 rounded-lg text-white font-medium ${getDifficultyColor(deck.difficulty)} text-xs`}>
                              {getDifficultyLabel(deck.difficulty)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                        <div className="flex items-center gap-2 order-2 sm:order-1">
                          {deck.isPublic ? (
                            <div className="p-1.5 sm:p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                              <Users className="text-green-600 dark:text-green-400" size={14} />
                            </div>
                          ) : (
                            <div className="p-1.5 sm:p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                              <Lock className="text-gray-500 dark:text-gray-400" size={14} />
                            </div>
                          )}
                          {deck.isFavorite && (
                            <div className="p-1.5 sm:p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                              <Star className="text-amber-500 fill-current" size={14} />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto order-1 sm:order-2">
                          <Link 
                            to={`/flashcards/study/${deck.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 sm:flex-none"
                          >
                            <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 sm:px-4 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 w-full">
                              <Play size={14} />
                              <span>Çalış</span>
                            </button>
                          </Link>
                          <Link 
                            to={`/flashcards/deck/${deck.id}/settings`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button className="p-2 bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200">
                              <Settings size={14} />
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
               </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Modern Review Modal */}
      <AnimatePresence>
        {showReviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 max-w-2xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 mx-4"
            >
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white pr-4">
                  Tekrar Edilecek Kartları Seç
                </h2>
                <button
                  className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 text-lg flex-shrink-0"
                  onClick={() => setShowReviewModal(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-700 dark:text-gray-300 mb-6 font-medium">
                  Hangi dersten tekrar kartlarını çalışmak istiyorsunuz?
                </p>
                
                <div className="grid grid-cols-1 gap-3 max-h-[50vh] overflow-y-auto pr-2 scrollbar-hide">
                  {subjects.map(subject => {
                    const subjectDecks = decks.filter(deck => deck.subject === subject.value)
                    if (subjectDecks.length === 0) return null
                    
                    return (
                      <button
                        key={subject.value}
                        onClick={() => {
                          setShowReviewModal(false)
                          navigate(`/flashcards/review/${subject.value}`)
                        }}
                        className={`group relative overflow-hidden p-4 rounded-2xl text-left transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg bg-gradient-to-r ${subject.color} text-white dark:text-white`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-2xl">{subject.icon}</div>
                            <div>
                              <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{subject.label}</h3>
                              <p className="text-gray-700 dark:text-white/80 text-sm">{subjectDecks.length} deste mevcut</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <RotateCcw className="w-5 h-5" />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">Tekrar Et</span>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
                
                {decks.length === 0 && (
                  <div className="text-center py-8">
                    <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Henüz deste bulunmuyor
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Tekrar etmek için önce flashcard destesi oluşturmalısınız.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modern Import Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-3xl p-4 md:p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                  Flashcard Destelerini İçe Aktar
                </h2>
                <button
                  className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 text-lg"
                  onClick={() => setShowImportModal(false)}
                >
                  ✕
                </button>
              </div>
              <ImportFlashcards onComplete={() => {
                setShowImportModal(false)
                dispatch(fetchDecks() as any)
              }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Flashcards